import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ClipboardDocumentListIcon,
    ExclamationTriangleIcon,
    PrinterIcon,
} from '@heroicons/react/24/outline';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import Button from '@/components/Button/Button';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import { useToast } from '@/hooks/useToast';
import {
    EvaluationCollaboratorResult,
    EvaluationExpectedSeniorityComparison,
    EvaluationNumericSummary,
    EvaluationResultGroup,
    getEvaluationCollaboratorResult,
} from '../services/evaluationResults';
import {
    EvaluationQualitativeReportContent,
    generateEvaluationQualitativeReport,
} from '../services/evaluationQualitativeReport';
import { EvaluationInviteType } from '../services/evaluationsService';

const evaluatorTypeLabels: Record<EvaluationInviteType, string> = {
    SELF: 'Autoavaliação',
    LEADER: 'Líder',
    CLIENT: 'Cliente',
};

const comparisonColors = ['#1d4ed8', '#dc2626', '#f59e0b', '#16a34a', '#7c3aed'];

const scoreTone = (percentage: number | null) => {
    if (percentage === null) return 'border-gray-200 bg-gray-50 text-gray-500';
    if (percentage >= 0.85) return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    if (percentage >= 0.7) return 'border-blue-200 bg-blue-50 text-blue-700';
    if (percentage >= 0.5) return 'border-amber-200 bg-amber-50 text-amber-800';
    return 'border-red-200 bg-red-50 text-red-700';
};

const formatScore = (value?: number | null) => (
    typeof value === 'number' && Number.isFinite(value)
        ? value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
        : '-'
);

const formatPercent = (value?: number | null) => (
    typeof value === 'number' && Number.isFinite(value)
        ? value.toLocaleString('pt-BR', { style: 'percent', maximumFractionDigits: 0 })
        : '-'
);

const formatPoints = (summary: EvaluationNumericSummary) => (
    summary.count > 0
        ? `${formatScore(summary.score)} / ${formatScore(summary.maxScore)} pts`
        : 'Pendente'
);

const formatGap = (value?: number | null) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return '-';
    }

    return value > 0 ? `+${formatScore(value)}` : formatScore(value);
};

function normalizeText(value?: string | null): string {
    return (value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toUpperCase();
}

function isScoreGroup(group: EvaluationResultGroup): boolean {
    return !group.type || group.type === 'STANDARD';
}

function getExpectedForGroup(
    comparison: EvaluationExpectedSeniorityComparison,
    group: EvaluationResultGroup
): number | null {
    const groupKey = normalizeText(group.description);
    const match = Object.entries(comparison.thresholds)
        .find(([axis]) => normalizeText(axis) === groupKey);

    return typeof match?.[1] === 'number' ? match[1] : null;
}

function getIndicatorContext(result: EvaluationCollaboratorResult, indicatorId: string) {
    for (const group of result.groups) {
        for (const behavior of group.behaviors) {
            if (behavior.indicators.some((indicator) => indicator.id === indicatorId)) {
                return { group, behavior };
            }
        }
    }

    return null;
}

function PercentCell({ value }: { value?: number | null }) {
    return (
        <span className={`inline-flex min-w-[4rem] justify-center border px-2 py-1 text-xs font-semibold ${scoreTone(value ?? null)}`}>
            {formatPercent(value)}
        </span>
    );
}

function ReportPrintStyles() {
    return (
        <style>
            {`
                @page {
                    size: A4;
                    margin: 14mm 12mm 16mm;
                }

                @media print {
                    html,
                    body {
                        background: #ffffff !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        width: auto !important;
                        min-width: 0 !important;
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }

                    body * {
                        visibility: hidden !important;
                    }

                    .no-print {
                        display: none !important;
                        visibility: hidden !important;
                    }

                    .no-print * {
                        display: none !important;
                        visibility: hidden !important;
                    }

                    .evaluation-report-root {
                        background: #ffffff !important;
                        display: block !important;
                        left: 0 !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        position: absolute !important;
                        top: 0 !important;
                        visibility: visible !important;
                        width: 100% !important;
                    }

                    .evaluation-report-root > :not([hidden]) ~ :not([hidden]) {
                        margin-top: 0 !important;
                    }

                    .evaluation-report-root * {
                        visibility: visible !important;
                    }

                    .evaluation-report-page {
                        background: #ffffff !important;
                        padding: 0 !important;
                        border: 0 !important;
                        break-after: auto;
                        box-sizing: border-box !important;
                        display: block !important;
                        margin: 0 0 10mm !important;
                        max-width: none !important;
                        min-height: 0 !important;
                        overflow: visible !important;
                        page-break-after: auto;
                        box-shadow: none !important;
                        width: 100% !important;
                        font-size: 11.5px !important;
                        line-height: 1.4 !important;
                    }

                    .evaluation-report-page:last-child {
                        margin-bottom: 0 !important;
                    }

                    .evaluation-report-page.print-break-after {
                        break-after: page;
                        margin-bottom: 0 !important;
                        page-break-after: always;
                    }

                    .evaluation-report-cover {
                        min-height: 260mm !important;
                    }

                    .evaluation-report-page h1 {
                        font-size: 28px !important;
                        line-height: 1.15 !important;
                    }

                    .evaluation-report-page h2 {
                        break-after: avoid;
                        font-size: 18px !important;
                        line-height: 1.2 !important;
                        page-break-after: avoid;
                    }

                    .evaluation-report-page h3 {
                        break-after: avoid;
                        font-size: 14px !important;
                        line-height: 1.25 !important;
                        page-break-after: avoid;
                    }

                    .evaluation-report-page h4,
                    .evaluation-report-page p,
                    .evaluation-report-page dt,
                    .evaluation-report-page dd,
                    .evaluation-report-page span,
                    .evaluation-report-page strong {
                        line-height: 1.35 !important;
                    }

                    .evaluation-report-page p {
                        orphans: 3;
                        widows: 3;
                    }

                    .print-section {
                        break-inside: auto;
                        page-break-inside: auto;
                    }

                    .print-keep,
                    .avoid-print-break {
                        break-inside: avoid;
                        break-inside: avoid-page;
                        page-break-inside: avoid;
                    }

                    .evaluation-report-page .overflow-x-auto {
                        overflow: visible !important;
                    }

                    .evaluation-report-page table {
                        break-inside: auto;
                        max-width: 100% !important;
                        min-width: 0 !important;
                        page-break-inside: auto;
                        table-layout: fixed !important;
                        width: 100% !important;
                    }

                    .evaluation-report-page thead {
                        display: table-header-group;
                    }

                    .evaluation-report-page tbody {
                        display: table-row-group;
                    }

                    .evaluation-report-page tr {
                        break-inside: avoid;
                        break-inside: avoid-page;
                        page-break-inside: avoid;
                    }

                    .evaluation-report-page th,
                    .evaluation-report-page td {
                        font-size: 9.5px !important;
                        line-height: 1.25 !important;
                        overflow-wrap: anywhere !important;
                        padding: 4px 5px !important;
                        vertical-align: top !important;
                        white-space: normal !important;
                    }

                    .evaluation-report-page .inline-flex {
                        min-width: 0 !important;
                    }
                }
            `}
        </style>
    );
}

function ReportPage({
    children,
    className = '',
    printBreakAfter = false,
}: {
    children: React.ReactNode;
    className?: string;
    printBreakAfter?: boolean;
}) {
    return (
        <section className={`evaluation-report-page mx-auto w-full max-w-[960px] border border-gray-200 bg-white px-8 py-10 shadow-sm ${printBreakAfter ? 'print-break-after' : ''} ${className}`}>
            {children}
        </section>
    );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
    return (
        <h2 className="border-b-2 border-blue-700 pb-2 text-2xl font-bold text-blue-800">
            {children}
        </h2>
    );
}

function CoverPage({ result, period }: { result: EvaluationCollaboratorResult; period: string }) {
    const roleLabel = [
        result.collaborator.positionName,
        result.collaborator.seniorityName,
    ].filter(Boolean).join(' ');
    const projectLabel = [
        result.collaborator.clientName,
        result.collaborator.projectName,
    ].filter(Boolean).join(' / ')
        || result.collaborator.clientResponsibleName;

    return (
        <ReportPage className="evaluation-report-cover flex min-h-[720px] flex-col" printBreakAfter>
            <div className="mt-12 text-center">
                <h1 className="text-4xl font-bold uppercase tracking-normal text-blue-800">Feedback de Desempenho</h1>
                <p className="mt-3 text-xl text-gray-600">{result.campaign.questionnaireTitle || 'Avaliação de Desempenho'}</p>
            </div>

            <div className="mt-16 border-t-2 border-blue-700 pt-12">
                <dl className="mx-auto grid max-w-[720px] grid-cols-[180px_minmax(0,1fr)] gap-x-8 gap-y-5 text-lg">
                    <dt className="font-bold text-gray-600">Colaborador:</dt>
                    <dd className="text-gray-900">{result.collaborator.collaboratorName}</dd>

                    <dt className="font-bold text-gray-600">Cargo / Nível:</dt>
                    <dd className="text-gray-900">{roleLabel || '-'}</dd>

                    <dt className="font-bold text-gray-600">Projeto:</dt>
                    <dd className="text-gray-900">{projectLabel || '-'}</dd>

                    <dt className="font-bold text-gray-600">Líder:</dt>
                    <dd className="text-gray-900">{result.collaborator.responsibleName || '-'}</dd>

                    <dt className="font-bold text-gray-600">Período:</dt>
                    <dd className="text-gray-900">{period}</dd>
                </dl>
            </div>
        </ReportPage>
    );
}

function SummaryTile({
    label,
    summary,
    tone,
}: {
    label: string;
    summary: EvaluationNumericSummary;
    tone: 'primary' | 'self' | 'leader' | 'client';
}) {
    const toneClasses = {
        primary: 'bg-blue-800 text-white',
        self: 'bg-blue-50 text-blue-800',
        leader: 'bg-amber-50 text-amber-900',
        client: 'bg-gray-100 text-gray-500',
    };
    const hasScore = summary.count > 0;

    return (
        <div className={`min-h-[94px] border border-gray-200 px-4 py-3 text-center ${toneClasses[tone]}`}>
            <p className="text-sm font-bold">{label}</p>
            <p className="mt-1 text-4xl font-bold leading-none">{hasScore ? formatPercent(summary.percentage) : '-'}</p>
            <p className="mt-2 text-sm opacity-85">{formatPoints(summary)}</p>
        </div>
    );
}

function SummaryStrip({ result }: { result: EvaluationCollaboratorResult }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryTile label="Média Geral" summary={result.overall.mean} tone="primary" />
            <SummaryTile label="Autoavaliação" summary={result.overall.byType.SELF} tone="self" />
            <SummaryTile label="Avaliação Líder" summary={result.overall.byType.LEADER} tone="leader" />
            <SummaryTile label="Avaliação Cliente" summary={result.overall.byType.CLIENT} tone="client" />
        </div>
    );
}

function AxisPerformance({
    result,
    scoreGroups,
}: {
    result: EvaluationCollaboratorResult;
    scoreGroups: EvaluationResultGroup[];
}) {
    const seniorityLabel = result.collaborator.seniorityName || '-';

    return (
        <div className="print-section">
            <h3 className="text-xl font-bold text-gray-900">Desempenho por Eixo</h3>
            <p className="mt-2 text-base text-gray-700">
                Resultado médio consolidado comparado ao parâmetro esperado pela Raro para {[
                    result.collaborator.positionName,
                    result.collaborator.seniorityName,
                ].filter(Boolean).join(' ') || 'o cargo atual'}.
            </p>

            <div className="mt-8 space-y-4">
                {scoreGroups.map((group) => {
                    const percentage = group.aggregate.mean.percentage ?? 0;

                    return (
                        <div key={group.id} className="grid grid-cols-[150px_minmax(0,1fr)_64px_88px] items-center gap-3 text-base">
                            <span className="text-gray-700">{group.description}</span>
                            <div className="h-6 bg-gray-200">
                                <div
                                    className="h-6 bg-blue-500"
                                    style={{ width: `${Math.max(0, Math.min(100, percentage * 100))}%` }}
                                />
                            </div>
                            <span className="font-bold text-blue-600">{formatPercent(group.aggregate.mean.percentage)}</span>
                            <span className="text-sm text-gray-500">
                                {group.expectedPercentage !== null ? `esp. ${formatPercent(group.expectedPercentage)}` : ''}
                            </span>
                        </div>
                    );
                })}

                <div className="grid grid-cols-[150px_minmax(0,1fr)_minmax(120px,auto)] items-center gap-3 pt-8 text-base">
                    <span className="text-gray-700">Prólogo (Senioridade)</span>
                    <div className="h-6 bg-gray-200">
                        <div className="h-6 w-2/5 bg-gray-500" />
                    </div>
                    <span className="font-bold text-gray-600">{seniorityLabel}</span>
                </div>
            </div>
        </div>
    );
}

function RaroSkillsCharts({
    result,
    scoreGroups,
}: {
    result: EvaluationCollaboratorResult;
    scoreGroups: EvaluationResultGroup[];
}) {
    const scoreChartData = scoreGroups.map((group) => ({
        group: group.description,
        media: group.aggregate.mean.percentage !== null ? Math.round(group.aggregate.mean.percentage * 100) : null,
        auto: group.aggregate.byType.SELF.percentage !== null ? Math.round(group.aggregate.byType.SELF.percentage * 100) : null,
        lider: group.aggregate.byType.LEADER.percentage !== null ? Math.round(group.aggregate.byType.LEADER.percentage * 100) : null,
        cliente: group.aggregate.byType.CLIENT.percentage !== null ? Math.round(group.aggregate.byType.CLIENT.percentage * 100) : null,
    }));
    const comparisons = result.expectedComparisons.slice(0, 5);
    const comparisonChartData = scoreGroups.map((group) => {
        const item: Record<string, number | string | null> = {
            group: group.description,
            media: group.aggregate.mean.percentage !== null ? Math.round(group.aggregate.mean.percentage * 100) : null,
        };

        comparisons.forEach((comparison, index) => {
            const value = getExpectedForGroup(comparison, group);
            item[`seniority_${index}`] = value !== null ? Math.round(value * 100) : null;
        });

        return item;
    });
    const hasClient = scoreChartData.some((item) => item.cliente !== null);

    if (scoreGroups.length === 0) {
        return null;
    }

    return (
        <div className="print-section mt-8 border border-gray-300 p-3">
            <div className="border-b border-yellow-300 bg-yellow-100 px-3 py-1 text-center text-xs font-bold uppercase text-gray-800">
                Gráfico Raro Skills
            </div>
            <div className="grid grid-cols-1 gap-4 pt-4 lg:grid-cols-2">
                <div className="print-keep h-[260px] border border-gray-200 p-3">
                    <h4 className="text-sm font-semibold uppercase text-gray-500">Avaliações e Média</h4>
                    <ResponsiveContainer width="100%" height="88%">
                        <LineChart data={scoreChartData} margin={{ top: 20, right: 18, left: 0, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="group" tick={{ fontSize: 11 }} />
                            <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(value) => [`${value}%`, '']} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line name="Avaliação líder" type="monotone" dataKey="lider" stroke="#2563eb" strokeWidth={2} connectNulls />
                            <Line name="Autoavaliação" type="monotone" dataKey="auto" stroke="#ef4444" strokeWidth={2} connectNulls />
                            {hasClient ? (
                                <Line name="Cliente" type="monotone" dataKey="cliente" stroke="#16a34a" strokeWidth={2} connectNulls />
                            ) : null}
                            <Line name="Média" type="monotone" dataKey="media" stroke="#f59e0b" strokeWidth={2} connectNulls />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="print-keep h-[260px] border border-gray-200 p-3">
                    <h4 className="text-sm font-semibold uppercase text-gray-500">Senioridade Raro x Pontuação</h4>
                    <ResponsiveContainer width="100%" height="88%">
                        <BarChart data={comparisonChartData} margin={{ top: 20, right: 10, left: 0, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="group" tick={{ fontSize: 11 }} />
                            <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(value) => [`${value}%`, '']} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {comparisons.map((comparison, index) => (
                                <Bar
                                    key={comparison.seniorityId || comparison.seniorityName}
                                    name={comparison.seniorityName}
                                    dataKey={`seniority_${index}`}
                                    fill={comparisonColors[index % comparisonColors.length]}
                                />
                            ))}
                            <Bar name="Média" dataKey="media" fill="#16a34a" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

function LowScoreTable({ result }: { result: EvaluationCollaboratorResult }) {
    return (
        <div className="print-section">
            <h3 className="text-xl font-bold text-gray-900">Menores Médias Individuais</h3>
            <div className="mt-6 w-full overflow-x-auto">
                <table className="w-full min-w-[700px] border-collapse text-sm">
                    <thead>
                        <tr className="bg-blue-800 text-white">
                            <th className="border border-white/40 px-3 py-2 text-left font-bold">Competência</th>
                            <th className="border border-white/40 px-3 py-2 text-left font-bold">Indicador</th>
                            <th className="border border-white/40 px-3 py-2 text-center font-bold">%</th>
                            <th className="border border-white/40 px-3 py-2 text-center font-bold">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {result.lowScoreIndicators.slice(0, 5).map((indicator) => {
                            const context = getIndicatorContext(result, indicator.id);
                            const expected = context?.group.expectedPercentage;
                            const shouldDevelop = indicator.percentage !== null
                                && (indicator.percentage < 0.7 || (expected !== null && expected !== undefined && indicator.percentage < expected));

                            return (
                                <tr key={indicator.id} className="odd:bg-white even:bg-gray-50">
                                    <td className="border border-gray-200 px-3 py-2 text-gray-900">{indicator.description}</td>
                                    <td className="border border-gray-200 px-3 py-2 text-gray-600">{context?.group.description || '-'}</td>
                                    <td className="border border-gray-200 px-3 py-2 text-center font-bold text-red-700">{formatPercent(indicator.percentage)}</td>
                                    <td className="border border-gray-200 px-3 py-2 text-center text-amber-800">{shouldDevelop ? 'Desenvolver' : 'Acompanhar'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function GapTable({ result, gapEvaluatorLabel }: { result: EvaluationCollaboratorResult; gapEvaluatorLabel: string }) {
    return (
        <div className="print-section mt-10">
            <h3 className="text-xl font-bold text-gray-900">Gaps: Autoavaliação x Avaliação do {gapEvaluatorLabel}</h3>
            <p className="mt-2 text-base text-gray-700">
                Diferenças entre a percepção do colaborador e a avaliação externa, ordenadas pelo maior gap absoluto.
            </p>
            <div className="mt-6 w-full overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse text-sm">
                    <thead>
                        <tr className="bg-blue-800 text-white">
                            <th className="border border-white/40 px-3 py-2 text-left font-bold">Competência</th>
                            <th className="border border-white/40 px-3 py-2 text-center font-bold">Autoavaliação</th>
                            <th className="border border-white/40 px-3 py-2 text-center font-bold">{gapEvaluatorLabel}</th>
                            <th className="border border-white/40 px-3 py-2 text-center font-bold">Média %</th>
                            <th className="border border-white/40 px-3 py-2 text-center font-bold">Gap</th>
                        </tr>
                    </thead>
                    <tbody>
                        {result.largeGapIndicators.slice(0, 15).map((indicator) => {
                            const gap = indicator.gapSelfEvaluator ?? 0;
                            const evaluatorScore = indicator.gapEvaluatorType
                                ? indicator.answers[indicator.gapEvaluatorType]
                                : null;

                            return (
                                <tr key={indicator.id} className="odd:bg-white even:bg-gray-50">
                                    <td className="border border-gray-200 px-3 py-2 text-gray-900">{indicator.description}</td>
                                    <td className="border border-gray-200 px-3 py-2 text-center text-blue-700">{formatScore(indicator.answers.SELF)}</td>
                                    <td className="border border-gray-200 px-3 py-2 text-center text-amber-800">{formatScore(evaluatorScore)}</td>
                                    <td className="border border-gray-200 px-3 py-2 text-center font-bold text-gray-800">{formatPercent(indicator.percentage)}</td>
                                    <td className={`border border-gray-200 px-3 py-2 text-center font-bold ${gap < 0 ? 'text-red-700' : 'text-green-700'}`}>
                                        {formatGap(indicator.gapSelfEvaluator)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function BehaviorIndicatorsAnalysisTable({
    scoreGroups,
    visibleTypes,
}: {
    scoreGroups: EvaluationResultGroup[];
    visibleTypes: EvaluationInviteType[];
}) {
    const rows = scoreGroups.flatMap((group) => (
        group.behaviors.map((behavior) => ({ group, behavior }))
    ));

    return (
        <div>
            <SectionTitle>Indicadores comportamentais usados na análise</SectionTitle>
            <p className="mt-4 text-base leading-relaxed text-gray-700">
                Estes indicadores são a base quantitativa utilizada para gerar a análise qualitativa, os pontos de excelência, os pontos de melhoria e as ações de PDI.
            </p>
            <div className="mt-6 w-full overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse text-sm">
                    <thead>
                        <tr className="bg-blue-800 text-white">
                            <th className="border border-white/40 px-3 py-2 text-left font-bold">Eixo</th>
                            <th className="border border-white/40 px-3 py-2 text-left font-bold">Comportamento</th>
                            {visibleTypes.map((type) => (
                                <th key={type} className="border border-white/40 px-3 py-2 text-center font-bold">
                                    {evaluatorTypeLabels[type]}
                                </th>
                            ))}
                            <th className="border border-white/40 px-3 py-2 text-center font-bold">Média %</th>
                            <th className="border border-white/40 px-3 py-2 text-center font-bold">Gap</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(({ group, behavior }) => (
                            <tr key={`${group.id}-${behavior.id}`} className="odd:bg-white even:bg-gray-50">
                                <td className="border border-gray-200 px-3 py-2 font-medium text-gray-900">{group.description}</td>
                                <td className="border border-gray-200 px-3 py-2 text-gray-700">{behavior.description}</td>
                                {visibleTypes.map((type) => (
                                    <td key={type} className="border border-gray-200 px-3 py-2 text-center text-gray-700">
                                        {formatScore(behavior.aggregate.byType[type].average)}
                                    </td>
                                ))}
                                <td className="border border-gray-200 px-3 py-2 text-center font-bold text-gray-800">
                                    {formatPercent(behavior.aggregate.mean.percentage)}
                                </td>
                                <td className={`border border-gray-200 px-3 py-2 text-center font-bold ${(behavior.gapSelfEvaluator ?? 0) < 0 ? 'text-red-700' : 'text-green-700'}`}>
                                    {formatGap(behavior.gapSelfEvaluator)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ExpectedTable({
    scoreGroups,
    expectedContextLabel,
}: {
    scoreGroups: EvaluationResultGroup[];
    expectedContextLabel: string;
}) {
    return (
        <div className="print-section mt-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-xl font-bold text-gray-900">Esperado pela Raro</h3>
                {expectedContextLabel ? (
                    <span className="border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-600">
                        {expectedContextLabel}
                    </span>
                ) : null}
            </div>
            <div className="mt-4 w-full overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">Eixo</th>
                            <th className="border border-gray-200 px-4 py-3 text-center font-semibold text-gray-700">Média</th>
                            <th className="border border-gray-200 px-4 py-3 text-center font-semibold text-gray-700">Esperado pela Raro</th>
                            <th className="border border-gray-200 px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {scoreGroups.map((group) => (
                            <tr key={group.id} className="odd:bg-white even:bg-gray-50">
                                <td className="border border-gray-200 px-4 py-3 font-medium text-gray-900">{group.description}</td>
                                <td className="border border-gray-200 px-4 py-3 text-center"><PercentCell value={group.aggregate.mean.percentage} /></td>
                                <td className="border border-gray-200 px-4 py-3 text-center">{formatPercent(group.expectedPercentage)}</td>
                                <td className="border border-gray-200 px-4 py-3 text-center">
                                    {group.expectedPercentage === null ? (
                                        <span className="border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-500">Sem referência</span>
                                    ) : group.belowExpected ? (
                                        <span className="border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800">Abaixo</span>
                                    ) : (
                                        <span className="border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">Dentro</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SignatureBlock({ result }: { result: EvaluationCollaboratorResult }) {
    return (
        <div className="avoid-print-break mt-10 grid grid-cols-1 border border-gray-200 text-center text-gray-600 sm:grid-cols-2">
            <div className="border-b border-gray-200 p-6 sm:border-b-0 sm:border-r">
                <p>Assinatura do Colaborador</p>
                <div className="mt-16 border-t border-gray-400 pt-2">
                    <p>{result.collaborator.collaboratorName}</p>
                    <p>Data: ___/___/______</p>
                </div>
            </div>
            <div className="p-6">
                <p>Assinatura do Líder</p>
                <div className="mt-16 border-t border-gray-400 pt-2">
                    <p>{result.collaborator.responsibleName || '-'}</p>
                    <p>Data: ___/___/______</p>
                </div>
            </div>
        </div>
    );
}

function QualitativeReportOverview({
    result,
    report,
}: {
    result: EvaluationCollaboratorResult;
    report: EvaluationQualitativeReportContent;
}) {
    return (
        <div className="space-y-8">
            <section className="print-section">
                <SectionTitle>2. Visão Geral do Resultado</SectionTitle>
                <div className="mt-7 space-y-5 text-lg leading-relaxed text-gray-800">
                    {report.overview.map((paragraph, index) => (
                        <p key={`overview-${index}`}>{paragraph}</p>
                    ))}
                </div>
            </section>

            <section className="print-section">
                <SectionTitle>3. Pontos de Excelência</SectionTitle>
                <p className="mt-4 text-lg leading-relaxed text-gray-800">
                    Comportamentos e competências com desempenho consistente, a reconhecer e manter:
                </p>
                <div className="mt-8 space-y-5">
                    {report.excellence.map((item, index) => (
                        <div key={`excellence-${index}`} className="avoid-print-break">
                            <h3 className="text-lg font-bold text-green-800">{item.title}</h3>
                            <p className="mt-2 text-base leading-relaxed text-gray-800">{item.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="print-section">
                <SectionTitle>4. Pontos de Melhoria</SectionTitle>
                <p className="mt-4 text-lg leading-relaxed text-gray-800">
                    Competências com maior oportunidade de evolução, identificadas pelos indicadores quantitativos e pela diferença entre percepções:
                </p>
                <div className="mt-8 space-y-5">
                    {report.improvements.map((item, index) => (
                        <div key={`improvement-${index}`} className="avoid-print-break">
                            <h3 className="text-lg font-bold text-amber-900">{item.title}</h3>
                            <p className="mt-2 text-base leading-relaxed text-gray-800">{item.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="print-section">
                <SectionTitle>5. Plano de Desenvolvimento Individual (PDI)</SectionTitle>
                <p className="mt-4 text-lg leading-relaxed text-gray-800">
                    Ações de desenvolvimento sugeridas para o próximo ciclo, a discutir e acordar com o líder {result.collaborator.responsibleName || 'responsável'}:
                </p>
                <div className="mt-7 space-y-5">
                    {report.pdi.map((item, index) => (
                        <div key={`pdi-${index}`} className="avoid-print-break grid grid-cols-[160px_minmax(0,1fr)] border border-gray-200 text-base">
                            <div className="flex items-center justify-center bg-blue-50 px-4 py-6 font-bold text-blue-800">
                                Ação {index + 1}
                            </div>
                            <div className="px-5 py-4 leading-relaxed text-gray-800">
                                <strong>{item.title}</strong>
                                {' - '}
                                {item.body}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="print-section">
                <SectionTitle>6. Considerações Finais</SectionTitle>
                <div className="mt-7 space-y-5 text-lg leading-relaxed text-gray-800">
                    {report.finalConsiderations.map((paragraph, index) => (
                        <p key={`final-${index}`}>{paragraph}</p>
                    ))}
                </div>
                <SignatureBlock result={result} />
            </section>
        </div>
    );
}

function ReportOverview({
    result,
    qualitativeReport,
    qualitativeLoading,
    qualitativeError,
}: {
    result: EvaluationCollaboratorResult;
    qualitativeReport?: EvaluationQualitativeReportContent | null;
    qualitativeLoading: boolean;
    qualitativeError?: string | null;
}) {
    if (qualitativeReport) {
        return <QualitativeReportOverview result={result} report={qualitativeReport} />;
    }

    return (
        <section className={`border p-6 ${qualitativeLoading ? 'border-blue-200 bg-blue-50' : 'border-red-200 bg-red-50'}`}>
            <div className="flex items-start gap-3">
                <ExclamationTriangleIcon
                    className={`mt-1 h-6 w-6 ${qualitativeLoading ? 'text-blue-700' : 'text-red-700'}`}
                    aria-hidden
                />
                <div>
                    <SectionTitle>Textos qualitativos indisponíveis</SectionTitle>
                    <p className={`mt-5 text-base leading-relaxed ${qualitativeLoading ? 'text-blue-900' : 'text-red-900'}`}>
                        {qualitativeLoading
                            ? 'Os textos qualitativos ainda estão sendo gerados. Aguarde a conclusão antes de imprimir ou emitir este relatório.'
                            : qualitativeError || 'Não foi possível carregar os textos qualitativos da avaliação.'}
                    </p>
                    {!qualitativeLoading ? (
                        <p className="mt-3 text-sm text-red-800">
                            O relatório não usa fallback automático. Corrija a geração qualitativa e tente novamente.
                        </p>
                    ) : null}
                </div>
            </div>
        </section>
    );
}

export const EvaluationCollaboratorPrintReportPage: React.FC = () => {
    const { id, collaboratorId } = useParams<{ id: string; collaboratorId: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [result, setResult] = useState<EvaluationCollaboratorResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [qualitativeReport, setQualitativeReport] = useState<EvaluationQualitativeReportContent | null>(null);
    const [qualitativeLoading, setQualitativeLoading] = useState(false);
    const [qualitativeCached, setQualitativeCached] = useState<boolean | null>(null);
    const [qualitativeError, setQualitativeError] = useState<string | null>(null);

    const loadResult = useCallback(async () => {
        if (!id || !collaboratorId) return;

        setLoading(true);
        try {
            const data = await getEvaluationCollaboratorResult(id, collaboratorId);
            setResult(data);
            setLoadError(null);
            if (!data) {
                setLoadError('Não encontramos este colaborador dentro da campanha.');
            }
        } catch (error) {
            console.error(error);
            setResult(null);
            setLoadError('Não foi possível carregar o resultado individual.');
            toast.error('Erro', 'Não foi possível carregar o resultado individual.');
        } finally {
            setLoading(false);
        }
    }, [collaboratorId, id, toast]);

    useEffect(() => {
        void loadResult();
    }, [loadResult]);

    useEffect(() => {
        if (!id || !collaboratorId || !result) {
            return;
        }

        let cancelled = false;
        setQualitativeLoading(true);
        setQualitativeError(null);

        generateEvaluationQualitativeReport({
            campaignId: id,
            collaboratorId,
        })
            .then((data) => {
                if (cancelled) return;
                setQualitativeReport(data.content);
                setQualitativeCached(data.cached);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error(error);
                setQualitativeReport(null);
                setQualitativeCached(null);
                setQualitativeError('Não foi possível gerar os textos qualitativos agora.');
                toast.error('Textos qualitativos indisponíveis', 'O relatório não será emitido com textos automáticos.');
            })
            .finally(() => {
                if (!cancelled) {
                    setQualitativeLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [collaboratorId, id, result, toast]);

    const visibleTypes = useMemo<EvaluationInviteType[]>(() => {
        if (!result) return ['SELF', 'LEADER'];
        return result.overall.byType.CLIENT.count > 0
            ? ['SELF', 'LEADER', 'CLIENT']
            : ['SELF', 'LEADER'];
    }, [result]);

    if (loading) {
        return (
            <div className="border border-gray-200 bg-white p-8 shadow-sm">
                <p className="text-sm font-medium text-gray-900">Carregando resultado individual...</p>
                <p className="mt-2 text-sm text-gray-500">Consolidando respostas, médias e percentuais da campanha.</p>
            </div>
        );
    }

    if (loadError || !result) {
        return (
            <div className="border border-red-200 bg-red-50 p-8 shadow-sm">
                <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 text-red-600" aria-hidden />
                    <div className="space-y-4">
                        <div>
                            <h1 className="text-lg font-semibold text-red-900">Resultado não disponível</h1>
                            <p className="mt-1 text-sm text-red-800">{loadError}</p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Button variant="primary" onClick={() => void loadResult()}>
                                Tentar novamente
                            </Button>
                            <Button variant="outline" onClick={() => navigate(`/people/evaluations/${id}/results/${collaboratorId}`)}>
                                Voltar para resultado
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const period = `${result.campaign.startDate.toLocaleDateString('pt-BR')} - ${result.campaign.endDate.toLocaleDateString('pt-BR')}`;
    const scoreGroups = result.groups.filter(isScoreGroup);
    const gapEvaluatorLabel = result.overall.byType.LEADER.count === 0 && result.overall.byType.CLIENT.count > 0
        ? 'Cliente'
        : 'Líder';
    const expectedContextLabel = [
        result.collaborator.positionName,
        result.collaborator.seniorityName,
    ].filter(Boolean).join(' / ');

    return (
        <div className="evaluation-report-root space-y-6">
            <ReportPrintStyles />
            <div className="no-print">
                <SubPageHeader
                    backTo={`/people/evaluations/${id}/results/${collaboratorId}`}
                    icon={<ClipboardDocumentListIcon className="h-6 w-6" aria-hidden />}
                    badge="Relatório para impressão"
                    title={result.collaborator.collaboratorName}
                    description={`${result.campaign.questionnaireTitle || 'Avaliação de desempenho'} - ${period}`}
                    actions={
                        <Button
                            variant="outline"
                            disabled={!qualitativeReport}
                            onClick={() => window.print()}
                            leftIcon={<PrinterIcon className="h-4 w-4" aria-hidden />}
                        >
                            Imprimir
                        </Button>
                    }
                />
                <div className="mt-4 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm shadow-sm">
                    {qualitativeLoading ? (
                        <p className="font-medium text-blue-700">Gerando textos qualitativos com Gemini...</p>
                    ) : qualitativeReport ? (
                        <p className="font-medium text-emerald-700">
                            Textos qualitativos {qualitativeCached ? 'reaproveitados do cache' : 'gerados e salvos'}.
                        </p>
                    ) : (
                        <p className="font-medium text-red-700">
                            {qualitativeError || 'Textos qualitativos ainda não carregados.'}
                        </p>
                    )}
                </div>
            </div>

            <CoverPage result={result} period={period} />

            <ReportPage>
                <SectionTitle>1. Painel da Avaliação - Resultado Consolidado</SectionTitle>
                <div className="mt-9">
                    <SummaryStrip result={result} />
                </div>
                <div className="mt-10">
                    <AxisPerformance result={result} scoreGroups={scoreGroups} />
                    <RaroSkillsCharts result={result} scoreGroups={scoreGroups} />
                    <ExpectedTable scoreGroups={scoreGroups} expectedContextLabel={expectedContextLabel} />
                </div>
            </ReportPage>

            <ReportPage>
                <LowScoreTable result={result} />
                <GapTable result={result} gapEvaluatorLabel={gapEvaluatorLabel} />
            </ReportPage>

            <ReportPage>
                <BehaviorIndicatorsAnalysisTable
                    scoreGroups={scoreGroups}
                    visibleTypes={visibleTypes}
                />
            </ReportPage>

            <ReportPage>
                <ReportOverview
                    result={result}
                    qualitativeReport={qualitativeReport}
                    qualitativeLoading={qualitativeLoading}
                    qualitativeError={qualitativeError}
                />
            </ReportPage>
        </div>
    );
};
